/**
 * Video Effects — persistent full-frame overlays for the recorder, selected in
 * the Presentation panel like Filters/Frames. The chosen effect's `draw` runs
 * every frame onto the 600×600 filter canvas (the captureStream source), so it
 * bakes into the recording. No ML/segmentation — just cheap canvas compositing.
 */

export type VideoEffect = {
  id: string;
  name: string;
  /** CSS background for the picker swatch preview. */
  preview: string;
  /** Draw the overlay each frame. tSec = wall-clock seconds (for animation). Null = "none". */
  draw: ((ctx: CanvasRenderingContext2D, tSec: number) => void) | null;
};

function drawVignette(ctx: CanvasRenderingContext2D): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const g = ctx.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.18, W / 2, H / 2, Math.max(W, H) * 0.68);
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(0.45, 'rgba(0,0,0,0)');
  g.addColorStop(1, 'rgba(0,0,0,0.62)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
}

// Top→bottom colour wash (tint over the whole frame).
function makeWash(top: string, bottom: string): (ctx: CanvasRenderingContext2D) => void {
  return (ctx: CanvasRenderingContext2D) => {
    const W = ctx.canvas.width;
    const H = ctx.canvas.height;
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, top);
    g.addColorStop(1, bottom);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  };
}

// Full-frame film grain: build a small greyscale noise tile each frame and blend
// it (scaled up) over the video. A tile + scale is far cheaper than per-pixel
// noise at full resolution, and reads as real grain.
let grainCanvas: HTMLCanvasElement | null = null;
let grainCtx: CanvasRenderingContext2D | null = null;

function drawGrain(ctx: CanvasRenderingContext2D): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const N = 130; // smaller tile → coarser, less dense grain
  if (!grainCanvas) {
    grainCanvas = document.createElement('canvas');
    grainCanvas.width = N;
    grainCanvas.height = N;
    grainCtx = grainCanvas.getContext('2d');
  }
  if (!grainCtx) return;
  const img = grainCtx.createImageData(N, N);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const v = 40 + ((Math.random() * 175) | 0); // mid-band grey → grain, not harsh salt/pepper
    d[i] = d[i + 1] = d[i + 2] = v;
    d[i + 3] = 255;
  }
  grainCtx.putImageData(img, 0, 0);

  ctx.save();
  ctx.globalAlpha = 0.15;
  ctx.globalCompositeOperation = 'overlay'; // modulates the video → filmic grain
  ctx.drawImage(grainCanvas, 0, 0, W, H);
  ctx.restore();
}

const LEAKS = [
  { hue: '255,140,60', bx: 0.14, by: 0.18, r: 0.5, phase: 0 },
  { hue: '255,80,140', bx: 0.86, by: 0.82, r: 0.55, phase: 2 },
  { hue: '255,210,90', bx: 0.9, by: 0.14, r: 0.4, phase: 4 },
];

function drawLightLeak(ctx: CanvasRenderingContext2D, t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  ctx.save();
  ctx.globalCompositeOperation = 'screen'; // glow over the video
  for (const l of LEAKS) {
    const cx = (l.bx + Math.sin(t * 0.3 + l.phase) * 0.06) * W;
    const cy = (l.by + Math.cos(t * 0.25 + l.phase) * 0.06) * H;
    const rad = l.r * Math.min(W, H) * (0.9 + 0.1 * Math.sin(t * 0.5 + l.phase));
    const a = 0.32 + 0.1 * Math.sin(t * 0.7 + l.phase);
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
    g.addColorStop(0, `rgba(${l.hue},${a})`);
    g.addColorStop(1, `rgba(${l.hue},0)`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }
  ctx.restore();
}

// Duotone-ish grade: darken toward an indigo shadow, lift highlights toward pink.
function drawDuotone(ctx: CanvasRenderingContext2D): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  ctx.save();
  // 1. Strip the video's colour → greyscale, so the two tones read cleanly.
  ctx.globalCompositeOperation = 'saturation';
  ctx.fillStyle = 'hsl(0,0%,50%)';
  ctx.fillRect(0, 0, W, H);
  // 2. Highlights → pink (multiply caps white at this colour); shadows → deep
  //    indigo (screen lifts black to this colour). The shadow colour MUST be dark,
  //    or screen washes the whole frame to that colour.
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = 'rgb(255,120,165)';
  ctx.fillRect(0, 0, W, H);
  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = 'rgb(38,44,92)';
  ctx.fillRect(0, 0, W, H);
  ctx.restore();
}

// Retro / VHS: scanlines + a magenta tint + a drifting tracking band + light noise.
function drawVHS(ctx: CanvasRenderingContext2D, t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  ctx.save();
  // Warm magenta tint.
  ctx.fillStyle = 'rgba(120,40,90,0.12)';
  ctx.fillRect(0, 0, W, H);
  // Scanlines.
  ctx.fillStyle = 'rgba(0,0,0,0.2)';
  for (let y = 0; y < H; y += 3) ctx.fillRect(0, y, W, 1);
  // Drifting tracking band.
  const bandY = ((t * 0.14) % 1) * (H + 40) - 40;
  const grad = ctx.createLinearGradient(0, bandY, 0, bandY + H * 0.08);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.5, 'rgba(255,255,255,0.1)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, bandY, W, H * 0.08);
  // Light static.
  ctx.globalAlpha = 0.05;
  ctx.fillStyle = '#ffffff';
  for (let i = 0; i < 200; i++) ctx.fillRect(Math.random() * W, Math.random() * H, 1.5, 1.5);
  ctx.restore();
}

// Stage spotlight: warm glow on the centre, darkened edges.
function drawSpotlight(ctx: CanvasRenderingContext2D): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const g = ctx.createRadialGradient(W / 2, H * 0.42, Math.min(W, H) * 0.1, W / 2, H * 0.42, Math.max(W, H) * 0.72);
  g.addColorStop(0, 'rgba(255,244,214,0.14)');
  g.addColorStop(0.45, 'rgba(0,0,0,0)');
  g.addColorStop(1, 'rgba(0,0,0,0.52)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
}

const BOKEH = [
  { x: 0.2, y: 0.3, r: 0.22, hue: '255,180,120', ph: 0 },
  { x: 0.75, y: 0.25, r: 0.18, hue: '150,200,255', ph: 1.5 },
  { x: 0.5, y: 0.7, r: 0.26, hue: '255,150,190', ph: 3 },
  { x: 0.85, y: 0.72, r: 0.16, hue: '200,255,200', ph: 4.5 },
  { x: 0.12, y: 0.75, r: 0.2, hue: '255,220,140', ph: 6 },
  { x: 0.6, y: 0.15, r: 0.14, hue: '190,170,255', ph: 2.2 },
];

// Dreamy out-of-focus light circles drifting over the frame.
function drawBokeh(ctx: CanvasRenderingContext2D, t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  for (const b of BOKEH) {
    const x = (b.x + Math.sin(t * 0.14 + b.ph) * 0.05) * W;
    const y = (b.y + Math.cos(t * 0.11 + b.ph) * 0.05) * H;
    const r = b.r * Math.min(W, H) * (0.9 + 0.12 * Math.sin(t * 0.4 + b.ph));
    const a = Math.max(0, 0.15 + 0.06 * Math.sin(t * 0.5 + b.ph));
    const g = ctx.createRadialGradient(x, y, r * 0.35, x, y, r);
    g.addColorStop(0, `rgba(${b.hue},${a})`);
    g.addColorStop(0.7, `rgba(${b.hue},${a * 0.4})`);
    g.addColorStop(1, `rgba(${b.hue},0)`);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

const FLARE_GHOSTS = [
  { f: 0.35, r: 10, c: '120,200,255' },
  { f: 0.7, r: 18, c: '255,150,180' },
  { f: 1.15, r: 12, c: '180,255,180' },
  { f: 1.5, r: 22, c: '255,220,150' },
];

// Cinematic lens flare: a bright source glow + starburst rays + anamorphic streak
// + ghost circles toward the centre. `baseX` sets which side it lives on (0.62 =
// right, 0.38 = left); it slow-drifts around that anchor.
function makeLensFlare(baseX: number): (ctx: CanvasRenderingContext2D, t: number) => void {
  return (ctx: CanvasRenderingContext2D, t: number): void => {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  ctx.save();
  ctx.globalCompositeOperation = 'screen';
  // Gentle slow drift (two periods → subtle wander). Horizontal cycle ≈ 5 min.
  const sx = (baseX + Math.sin(t * 0.0209) * 0.18) * W;
  const sy = (0.13 + Math.cos(t * 0.0147) * 0.08) * H;
  const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, Math.min(W, H) * 0.32);
  g.addColorStop(0, 'rgba(255,240,205,0.55)');
  g.addColorStop(0.3, 'rgba(255,200,120,0.22)');
  g.addColorStop(1, 'rgba(255,180,80,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // Long horizontal anamorphic streak through the source.
  const streak = ctx.createLinearGradient(0, sy, W, sy);
  streak.addColorStop(0, 'rgba(150,195,255,0)');
  streak.addColorStop(0.5, 'rgba(185,215,255,0.32)');
  streak.addColorStop(1, 'rgba(150,195,255,0)');
  ctx.fillStyle = streak;
  ctx.fillRect(0, sy - 2, W, 4);

  // Starburst rays radiating from the source (bright at the flare, fading out).
  const nRays = 12;
  const maxLen = Math.max(W, H) * 0.42;
  ctx.lineCap = 'round';
  for (let i = 0; i < nRays; i++) {
    const ang = (i / nRays) * Math.PI * 2 + t * 0.04;
    const len = maxLen * (0.5 + 0.5 * (i % 3) / 2); // varied lengths
    const ex = sx + Math.cos(ang) * len;
    const ey = sy + Math.sin(ang) * len;
    const rg = ctx.createLinearGradient(sx, sy, ex, ey);
    rg.addColorStop(0, 'rgba(255,244,214,0.4)');
    rg.addColorStop(1, 'rgba(255,244,214,0)');
    ctx.strokeStyle = rg;
    ctx.lineWidth = i % 2 === 0 ? 2 : 1;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(ex, ey);
    ctx.stroke();
  }

  const cx = W / 2;
  const cy = H / 2;
  for (const gh of FLARE_GHOSTS) {
    const fx = sx + (cx - sx) * gh.f;
    const fy = sy + (cy - sy) * gh.f;
    const cg = ctx.createRadialGradient(fx, fy, 0, fx, fy, gh.r);
    cg.addColorStop(0, `rgba(${gh.c},0.18)`);
    cg.addColorStop(1, `rgba(${gh.c},0)`);
    ctx.fillStyle = cg;
    ctx.beginPath();
    ctx.arc(fx, fy, gh.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  };
}

// Digital glitch: intermittently shift horizontal slices of the frame + a
// stray colour bar. drawImage copies from the canvas itself (the current frame).
function drawGlitch(ctx: CanvasRenderingContext2D): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const cv = ctx.canvas;
  if (Math.random() < 0.45) {
    const n = 3 + Math.floor(Math.random() * 3);
    for (let i = 0; i < n; i++) {
      const y = Math.random() * H;
      const h = 3 + Math.random() * 16;
      const dx = (Math.random() - 0.5) * 44;
      ctx.drawImage(cv, 0, y, W, h, dx, y, W, h);
    }
  }
  if (Math.random() < 0.3) {
    ctx.save();
    ctx.globalCompositeOperation = 'screen';
    ctx.globalAlpha = 0.3;
    const y = Math.random() * H;
    ctx.fillStyle = Math.random() < 0.5 ? 'rgb(255,0,150)' : 'rgb(0,220,255)';
    ctx.fillRect(0, y, W, 2 + Math.random() * 10);
    ctx.restore();
  }
}

export const VIDEO_EFFECTS: VideoEffect[] = [
  { id: 'none', name: 'None', preview: 'repeating-linear-gradient(45deg,#161b2b,#161b2b 6px,#1c2234 6px,#1c2234 12px)', draw: null },
  {
    id: 'vignette',
    name: 'Vignette',
    preview: 'radial-gradient(circle at center, #6b7385 38%, #0a0d16 100%)',
    draw: drawVignette,
  },
  {
    id: 'cool',
    name: 'Cool',
    preview: 'linear-gradient(180deg,#5a96ff,#3c5ac8)',
    draw: makeWash('rgba(90,150,255,0.22)', 'rgba(60,90,200,0.28)'),
  },
  {
    id: 'sunset',
    name: 'Sunset',
    preview: 'linear-gradient(180deg,#ffa03c,#ff5a8c)',
    draw: makeWash('rgba(255,160,60,0.26)', 'rgba(255,80,140,0.28)'),
  },
  {
    id: 'duotone',
    name: 'Duotone',
    preview: 'linear-gradient(135deg,#2e3878,#ff6e96)',
    draw: drawDuotone,
  },
  {
    id: 'grain',
    name: 'Film Grain',
    preview: 'linear-gradient(180deg,#4a4f5a,#2a2f3a)',
    draw: drawGrain,
  },
  {
    id: 'lightleak',
    name: 'Light Leak',
    preview: 'linear-gradient(120deg,rgba(255,138,60,0.6),rgba(255,208,102,0.7),rgba(255,90,140,0.6)),#1a1420',
    draw: drawLightLeak,
  },
  {
    id: 'vhs',
    name: 'Retro VHS',
    preview: 'repeating-linear-gradient(0deg,#40204a,#40204a 3px,#2a1030 3px,#2a1030 6px)',
    draw: drawVHS,
  },
  {
    id: 'spotlight',
    name: 'Spotlight',
    preview: 'radial-gradient(circle at 50% 42%, #fff4d6 0%, #6b7385 45%, #0a0d16 100%)',
    draw: drawSpotlight,
  },
  {
    id: 'bokeh',
    name: 'Bokeh',
    preview: 'radial-gradient(circle at 28% 38%, rgba(255,180,120,0.85), transparent 42%), radial-gradient(circle at 74% 66%, rgba(150,200,255,0.85), transparent 42%), #14182a',
    draw: drawBokeh,
  },
  {
    id: 'flare',
    name: 'Flare (R)',
    preview: 'radial-gradient(circle at 72% 26%, #fff0cd, #ffb050 28%, #1a1524 72%)',
    draw: makeLensFlare(0.62),
  },
  {
    id: 'flarel',
    name: 'Flare (L)',
    preview: 'radial-gradient(circle at 22% 26%, #fff0cd, #ffb050 28%, #1a1524 72%)',
    draw: makeLensFlare(0.26),
  },
  {
    id: 'glitch',
    name: 'Glitch',
    preview: 'linear-gradient(180deg,#141a2b 0 30%,#ff0096 30% 36%,#141a2b 36% 60%,#00dcff 60% 66%,#141a2b 66%)',
    draw: drawGlitch,
  },
];
